import { TierType } from '@/helpers/entities/enums/v2/TierType';
import { BillingStatus } from '@/helpers/entities/enums/v2/BillingStatus';
import { shortDate, standardDate, standardDateAndTime } from '@/helpers/DateFormatter';
import { enumValueNormalizer } from '@/helpers/enum_value_normalizer';
export class BillingModelBase {
    /**
     * Formatted Billing Period Start Date
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {string}
     */
    get formattedBillingStartDate() {
        return shortDate(this.billingPeriodStartAt);
    }
    /**
     * Formatted Billing Period End Date
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {string}
     */
    get formattedBillingEndDate() {
        return shortDate(this.billingPeriodEndAt);
    }
    /**
     * Formatted Billing Period Due Date
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {string}
     */
    get formattedBillingDueDate() {
        return standardDate(this.billingPeriodDueAt);
    }
    /**
     * Humanized Billing Status
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {string}
     */
    get humanizedStatus() {
        return enumValueNormalizer(this.status);
    }
    /**
     * For Payment Status Validator
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {boolean}
     */
    get forPayment() {
        return this.status === BillingStatus.ForPayment;
    }
    /**
     * Payment Submitted Status Validator
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {boolean}
     */
    get paymentSubmitted() {
        return this.status === BillingStatus.PaymentSubmitted;
    }
    /**
     * Paid Status Validator
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {boolean}
     */
    get paid() {
        return this.status === BillingStatus.Paid;
    }
    /**
     * Check if Merchant has outstanding balance
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     */
    get withOutStandingBalance() {
        const zero = 0;
        return this.balance > zero;
    }
    /**
     * Check if Billing Tier is Enterprise
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.8.0}
     *
     * @return {boolean}
     */
    get enterpriseTier() {
        return this.tierType === TierType.Enterprise;
    }
    /**
     * Check if Billing Tier is Free
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.8.0}
     *
     * @return {boolean}
     */
    get freeTier() {
        return this.tierType === TierType.Free;
    }
    /**
     * Check if Billing is Payable
     * For Enterprise Mini Apps, we check if billing is not disabled while for
     *  Free Tier we ensure current amount is not `zero`. In case the current
     * amount was `zero` we check if Mini App has outstanding balance
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.11.0}
     */
    get payable() {
        const zero = 0;
        const notDisabled = !this.disabled;
        const hasAccumulatedFee = this.currentAmount !== zero;
        const withOutstandingBalance = this.balance !== zero;
        if (this.enterpriseTier) {
            return notDisabled;
        }
        if (hasAccumulatedFee) {
            return true;
        }
        return notDisabled && withOutstandingBalance;
    }
    /**
     * Formatted Payment Received Date
     * @author Angelo David <adavid@gorated.ph>
     * @since Version {0.65.1}
     *
     * @return {string}
     */
    get formattedPaymentReceivedAt() {
        if (this.paymentReceivedAt) {
            return standardDateAndTime(this.paymentReceivedAt);
        }
        return '';
    }
    /**
     * Humanized Tier Type
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.6.0}
     *
     * @return {string}
     */
    get humanizedTierType() {
        return enumValueNormalizer(this.tierType);
    }
}
